import { Box, DialogActions, DialogContent, Stack } from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { Mutation_Root, Project_Expert, Project_Expert_Insert_Input, Query_Root, User } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { PrimaryButton, SecondaryButton } from 'src/shared/button/Buttons'
import { GLOBAL_USER_ROLES, USER_STATUS } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { required } from 'src/shared/form/validation/validators'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { gql, useClient } from 'urql'

interface AddExternalExpertValues {
  userId: number
}

interface AddExternalExpertModalDialogProps {
  assignedExperts: Project_Expert[]
  onCancel: () => void
  onSuccess: () => void
  projectBaseId: number
}

const decorators = createDecorators()

const queryExternalExperts = gql`
  query fetchExternalExperts($role: String!, $status: String!) {
    user(where: { status: { _eq: $status }, user_roles: { role: { _eq: $role } } }) {
      id
      first_name
      last_name
      email
    }
  }
`
const mutationInsertProjectExpert = gql`
  mutation insertProjectExpert($user: [project_expert_insert_input!]!) {
    insert_project_expert(objects: $user) {
      affected_rows
      returning {
        id
      }
    }
  }
`

export const AddExternalExpertModalDialog = ({
  assignedExperts,
  onCancel,
  onSuccess,
  projectBaseId,
}: AddExternalExpertModalDialogProps): ReactElement => {
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const urqlClient = useClient()

  const [externalExperts, setExternalExperts] = useState<User[]>([])

  const externalExpertsOptions = useMemo(
    () =>
      externalExperts.map((item, index) => ({
        label: item.first_name ? `${item.first_name} ${item.last_name} (${item.email})` : item.email,
        value: item.id,
        sortNumber: index + 1,
      })) as Option[],
    [externalExperts],
  )

  useEffect(() => {
    const fetchExternalExperts = async () => {
      const { data, error } = await urqlClient
        .query<
          {
            user: Query_Root['user']
          },
          {
            role: string
            status: string
          }
        >(queryExternalExperts, { role: GLOBAL_USER_ROLES['PD-EXT_EVALUATION_TEAM_PGV'], status: USER_STATUS.ACTIVE })
        .toPromise()

      if (error) {
        notificationService.operationFailed()
        return
      }

      // Filter out already assigned experts from the list of external experts
      const filteredExperts =
        data?.user.filter((item) => !assignedExperts.some((expert) => expert.user.id === item.id)) ?? []
      setExternalExperts(filteredExperts)
    }

    fetchExternalExperts()
  }, [notificationService, urqlClient, assignedExperts])

  const handleSubmitLocal = async (values: AddExternalExpertValues): Promise<any> => {
    const insertObject: Project_Expert_Insert_Input = {
      project_base_id: projectBaseId,
      user_id: values.userId,
    }
    const { data, error } = await urqlClient
      .mutation<{ insert_project_expert: Mutation_Root['insert_project_expert'] }>(mutationInsertProjectExpert, {
        user: insertObject,
      })
      .toPromise()

    if (!error || data?.insert_project_expert?.affected_rows === 1) {
      notificationService.changesSaved()
      onSuccess()
    } else {
      notificationService.operationFailed()
    }
  }

  return (
    <>
      <Box>
        <Form<AddExternalExpertValues>
          onSubmit={handleSubmitLocal}
          decorators={decorators}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent>
                <Stack spacing={2}>
                  <AutoCompleteField
                    required
                    label={getMessage('label.project.permissions.field.expert')}
                    name="userId"
                    options={externalExpertsOptions}
                    validate={required()}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <SecondaryButton messageKey={'button.cancel'} onClick={onCancel} />
                <PrimaryButton messageKey={'button.add'} type="submit" />
              </DialogActions>
            </form>
          )}
        />
      </Box>
    </>
  )
}
